import { autoUpdate, computePosition, flip, offset, shift } from '@floating-ui/dom'
import { layoutViewportPlatform } from '@schmancy/overlay/overlay.soft-keyboard'
import { HostAriaLabelMixin, SchmancyFormField } from '@schmancy/mixins/index'
import { color } from '@schmancy/directives'
// Side-effect imports, not bindings: `<sch-input>` and `<schmancy-option>` are
// defined by these modules' own `@customElement` calls, and an import used only
// in type position is elided at compile time — which left the trigger an
// unupgraded unknown element whenever `schmancy-select` was imported alone.
// The types come from `HTMLElementTagNameMap`, which both modules declare.
import '@schmancy/form/fields/input/input'
import '@schmancy/primitives/option/option'
import { SchmancyTheme } from '@schmancy/theme/theme.interface'
import { css, html, PropertyValues, TemplateResult } from 'lit'
import { customElement, property, query, queryAssignedElements, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { BehaviorSubject, combineLatest, fromEvent, Subject, takeUntil } from 'rxjs'
import { tap, withLatestFrom } from 'rxjs/operators'

export type SchmancySelectChangeEvent = CustomEvent<{
	value: string | string[]
}>

/** @deprecated Use inline schmancy-chips for bounded choices or schmancy-autocomplete for searchable lists. */
@customElement('schmancy-select')
export class SchmancySelect extends HostAriaLabelMixin(SchmancyFormField()) {
	static styles = [
		css`
			:host {
				display: block;
				position: relative;
			}

			[role='listbox'] {
				overflow-y: auto;
				outline: none;
			}
		`,
	]

	// FACE wiring (formAssociated, internals, attachInternals) and
	// name/required/disabled/validationMessage/validateOn/touched/dirty/
	// pristine/submitted/markTouched/markSubmitted/formResetCallback/
	// formDisabledCallback/FIELD_CONNECT_EVENT come from SchmancyFormField.

	@property({ type: String }) placeholder = ''

	@property({ type: String, reflect: true })
	override get value(): string | string[] {
		return this.multi ? (this.selectedValues$?.value ?? []) : (this.selectedValue$?.value ?? '')
	}
	override set value(val: string | string[]) {
		if (!this.selectedValue$ || !this.selectedValues$) return
		if (this.multi) {
			const values = Array.isArray(val)
				? val
				: val
					? String(val)
							.split(',')
							.map(v => v.trim())
							.filter(Boolean)
					: []
			this.selectedValues$.next(values)
		} else {
			this.selectedValue$.next(val == null ? '' : String(val))
		}
	}

	@property({ type: Array })
	get values() {
		return [...this.selectedValues$.value]
	}
	set values(vals: string[]) {
		const values = Array.isArray(vals) ? [...vals] : []
		if (this.multi) this.value = values
		else this.selectedValues$.next(values)
	}

	@property({ type: Boolean }) multi = false
	// `label` and `hint` come from the mixin.
	// M3 aligned sizes: 24dp (xxs) → 32dp (xs) → 40dp (sm) → 48dp (md) → 56dp (lg)
	@property({ type: String }) size: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' = 'md'

	@state() private isOpen = false
	@state() private isValid = true

	// Store the initial/default value for reset behavior. Distinct from the
	// mixin's `defaultValue` (which is `string` only); select needs the wider
	// shape so resetForm can restore arrays for multi-select.
	@state() private selectDefaultValue: string | string[] = ''

	@query('ul') private ul!: HTMLUListElement
	@query('sch-input') private inputRef!: HTMLElementTagNameMap['sch-input']
	@queryAssignedElements({ flatten: true }) private options!: HTMLElementTagNameMap['schmancy-option'][]
	private cleanupPositioner?: () => void

	private options$ = new BehaviorSubject<HTMLElementTagNameMap['schmancy-option'][]>([])
	private selectedValue$ = new BehaviorSubject<string>('')
	private selectedValues$ = new BehaviorSubject<string[]>([])
	private optionSelect$ = new Subject<HTMLElementTagNameMap['schmancy-option']>()

	/** Distinct from the mixin's `touched` (fires on blur): gates dropdown-positioning
	 * side-effects on user-driven actions vs. programmatic state changes. */
	@state() userInteracted = false

	@state() private focusedOptionId = ''

	override connectedCallback() {
		super.connectedCallback()

		this.selectDefaultValue = this.value

		fromEvent<KeyboardEvent>(this, 'keydown').pipe(takeUntil(this.disconnecting)).subscribe(this.handleKeyDown)

		this.setupReactivePipelines()
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		this.cleanupPositioner?.()
	}

	firstUpdated(changed: Map<PropertyKey, unknown>) {
		// The mixin's firstUpdated captures the default `dirty` compares against;
		// skipped, the default `validateOn: 'dirty'` gate never opens.
		super.firstUpdated(changed)
		this.setupOptionsAccessibility()

		// No error until the user interacts.
		if (this.inputRef) {
			this.inputRef.error = false
		}
	}

	override updated(changedProps: PropertyValues) {
		super.updated(changedProps)

		if (changedProps.has('value')) {
			// FormData needs a scalar; the mixin's willUpdate already called
			// setFormValue with the raw value, override for multi-select's
			// comma-joined shape.
			const formValue = this.multi ? this.selectedValues$.value.join(',') : this.selectedValue$.value
			this.internals?.setFormValue(formValue)
		}

		if (changedProps.has('isOpen')) {
			if (this.isOpen) {
				this.positionDropdown()
			} else {
				this.cleanupPositioner?.()
			}
		}
	}

	private setupOptionsAccessibility() {
		this.options?.forEach((option, index) => {
			option.setAttribute('role', 'option')
			if (!option.id) {
				option.id = `${this.id}-option-${index}`
			}

			// -1: focusable programmatically, excluded from tab order.
			option.tabIndex = -1

			option.setAttribute(
				'aria-selected',
				String(
					this.multi ? this.selectedValues$.value.includes(option.value) : option.value === this.selectedValue$.value,
				),
			)
		})
	}

	private async positionDropdown() {
		const reference = this.renderRoot.querySelector('.trigger') as HTMLElement
		if (!reference || !this.ul) return

		this.cleanupPositioner = autoUpdate(reference, this.ul, async () => {
			const viewportHeight = window.innerHeight
			const triggerRect = reference.getBoundingClientRect()

			const spaceBelow = viewportHeight - triggerRect.bottom
			const spaceAbove = triggerRect.top

			const maxHeight = Math.max(Math.max(spaceBelow, spaceAbove) * 0.75, 150)

			const shouldFlip = spaceBelow < 200 && spaceAbove > spaceBelow

			this.ul.style.maxHeight = `${maxHeight}px`

			const { x, y } = await computePosition(reference, this.ul, {
				placement: shouldFlip ? 'top-start' : 'bottom-start',
				middleware: [offset(5), flip(), shift({ padding: 5 })],
				platform: layoutViewportPlatform,
			})

			Object.assign(this.ul.style, {
				left: `${x}px`,
				top: `${y}px`,
				position: 'absolute',
				width: `${reference.offsetWidth}px`,
			})
		})
	}

	private handleKeyDown = (e: KeyboardEvent) => {
		if (this.disabled) {
			return
		}

		if (!this.isOpen) {
			if (['Enter', ' ', 'ArrowDown'].includes(e.key)) {
				e.preventDefault()
				this.openDropdown(false)
			}
			return
		}

		const options = Array.from(this.options || [])
		const current = options.findIndex(o => o.id === this.focusedOptionId) ?? -1

		switch (e.key) {
			case 'Escape':
				e.preventDefault()
				this.closeDropdown()
				break
			case 'ArrowDown':
				e.preventDefault()
				this.focusOption(options, Math.min(current + 1, options.length - 1))
				break
			case 'ArrowUp':
				e.preventDefault()
				this.focusOption(options, Math.max(current - 1, 0))
				break
			case 'Home':
				e.preventDefault()
				this.focusOption(options, 0)
				break
			case 'End':
				e.preventDefault()
				this.focusOption(options, options.length - 1)
				break
			case 'Enter':
			case ' ':
				e.preventDefault()
				if (this.focusedOptionId) {
					const focusedOption = options.find(opt => opt.id === this.focusedOptionId)
					if (focusedOption) {
						this.handleOptionSelect(focusedOption.value)
					}
				}
				break
			case 'Tab':
				this.closeDropdown()
				break
		}
	}

	private focusOption(options: HTMLElementTagNameMap['schmancy-option'][], index: number) {
		const option = options[index]
		if (option) {
			option.focus()
			this.focusedOptionId = option.id

			const combobox = this.renderRoot.querySelector('.trigger')
			if (combobox) {
				combobox.setAttribute('aria-activedescendant', option.id)
			}

			// Scroll the option into view if it's outside the visible range.
			if (this.ul && option.offsetTop !== undefined) {
				const optionTop = option.offsetTop
				const optionHeight = option.offsetHeight
				const scrollTop = this.ul.scrollTop
				const ulHeight = this.ul.clientHeight

				if (optionTop < scrollTop) {
					this.ul.scrollTop = optionTop
				} else if (optionTop + optionHeight > scrollTop + ulHeight) {
					this.ul.scrollTop = optionTop + optionHeight - ulHeight
				}
			}
		}
	}

	private async openDropdown(report = false) {
		if (this.disabled) {
			return
		}

		// touched isn't set here — only on close, so errors show after interaction completes.
		this.isOpen = true
		await this.updateComplete

		const options = Array.from(this.options || [])
		const selectedIndex = this.multi ? 0 : options.findIndex(o => o.value === this.selectedValue$.value)

		this.focusOption(options, Math.max(selectedIndex, 0))

		// `report` opts into validation (e.g. a form submission); opening alone never validates.
		if (report) this.reportValidity()
	}

	private closeDropdown() {
		// touched only follows a real interaction — not a close with no prior selection/interaction.
		if (this.userInteracted) {
			this.touched = true
		}

		this.isOpen = false
		this.focusedOptionId = ''

		const combobox = this.renderRoot.querySelector<HTMLElement>('.trigger')
		if (combobox) {
			combobox.removeAttribute('aria-activedescendant')
			combobox?.focus()
		}

		if (this.userInteracted && this.shouldShowError()) {
			this.checkValidity()
		}
	}

	private setupReactivePipelines() {
		fromEvent<CustomEvent>(this, 'option-select')
			.pipe(
				tap(e => {
					e.stopPropagation()
					const option = this.options.find(o => o.value === e.detail.value)
					if (option) {
						this.optionSelect$.next(option)
					}
				}),
				takeUntil(this.disconnecting),
			)
			.subscribe()

		this.optionSelect$
			.pipe(
				withLatestFrom(this.selectedValue$, this.selectedValues$),
				tap(([option, _, currentValues]) => {
					this.userInteracted = true
					this.markTouched()
					// `dirty` is a mixin getter; setting `value` below triggers it

					if (this.multi) {
						const index = currentValues.indexOf(option.value)
						const newValues =
							index > -1
								? [...currentValues.slice(0, index), ...currentValues.slice(index + 1)]
								: [...currentValues, option.value]
						this.value = newValues
					} else {
						this.value = option.value
						this.closeDropdown()
					}

					this.setupOptionsAccessibility()
					this.fireChangeEvent()
				}),
				takeUntil(this.disconnecting),
			)
			.subscribe()

		this.options$
			.pipe(
				tap(options => {
					options.forEach((option, index) => {
						option.setAttribute('role', 'option')
						option.tabIndex = -1
						if (!option.id) {
							option.id = `${this.id}-option-${index}`
						}
					})
				}),
				takeUntil(this.disconnecting),
			)
			.subscribe()

		combineLatest([this.selectedValue$, this.selectedValues$, this.options$])
			.pipe(
				tap(([singleValue, multiValues, options]) => {
					if (options.length === 0) return

					if (this.multi) {
						options.forEach(option => {
							option.selected = multiValues.includes(option.value)
						})
					} else {
						options.forEach(option => {
							option.selected = option.value === singleValue
						})
					}
					this.requestUpdate()
				}),
				takeUntil(this.disconnecting),
			)
			.subscribe()
	}

	private handleOptionSelect(value: string) {
		// Called only from keyboard navigation.
		const option = this.options.find(o => o.value === value)
		if (option) {
			this.optionSelect$.next(option)
		}
	}

	private fireChangeEvent() {
		const value = this.multi ? this.selectedValues$.value : this.selectedValue$.value

		this.dispatchEvent(
			new CustomEvent<SchmancySelectChangeEvent['detail']>('change', {
				detail: { value },
				bubbles: true,
				composed: true,
			}),
		)

		this.checkValidity()
	}

	public checkValidity(): boolean {
		const isEmpty = this.multi ? this.selectedValues$.value.length === 0 : !this.selectedValue$.value
		// `@query` reads null until the first render, and the anchor argument is
		// a non-nullable optional: passing null threw right out of willUpdate,
		// killing the whole update for every required select. undefined is
		// "argument omitted".
		this.isValid = this.setFieldValidity(
			this.required && isEmpty ? { valueMissing: true } : {},
			this.errorMessages?.valueMissing ?? 'Please select an option.',
			this.inputRef ?? undefined,
		)

		if (this.inputRef && this.hasUpdated) {
			this.inputRef.error = this.error
			this.inputRef.hint = this.error ? this.validationMessage : this.hint
		}

		return this.isValid
	}

	public reportValidity(): boolean {
		// Unlike checkValidity, always surfaces the result to the user.
		const valid = this.checkValidity()

		if (this.inputRef) {
			this.inputRef.error = !valid
			// The rendered words wait behind the show-error gate; the platform's are the verdict's own.
			this.inputRef.hint = !valid ? this.validationMessage || (this.internals?.validationMessage ?? '') : this.hint

			if (!valid && !this.isOpen) {
				// Auto-open (without counting as user interaction) so invalid options are visible immediately.
				this.openDropdown(false)
			}

			// Native reportValidity only when invalid, to trigger the browser's validation popup.
			if (!valid) {
				this.inputRef.reportValidity()
			}
		}

		return valid
	}

	// `markTouched`, `markSubmitted`, `touched`, `dirty`, `pristine`, `submitted`,
	// `error` (storage) — all from the mixin. Select's `error` semantics
	// (errors-while-open suppression) live in render() via shouldShowError() +
	// !isOpen, not as a separate getter.

	/**
	 * Multi-value-aware `toFormEntries`. Single-select emits one `[name,value]`;
	 * multi-select emits N entries with the same name (canonical FormData shape
	 * for repeated fields).
	 */
	override toFormEntries(): Array<[string, FormDataEntryValue]> {
		if (!this.name || this.disabled) return []
		const v = this.value
		if (v === undefined || v === null || v === '') return []
		if (Array.isArray(v)) return v.map(item => [this.name, String(item)] as [string, FormDataEntryValue])
		return [[this.name, String(v)]]
	}

	override setCustomValidity(message: string) {
		super.setCustomValidity(message)
		// Re-run so the verdict, refusal included, carries the input as the anchor
		// the browser's own validation bubble points at.
		this.checkValidity()
	}

	/**
	 * Reset to the snapshot captured at connect time. Mixin's `resetForm`
	 * handles touched/submitted/error/validationMessage; the override layers on
	 * select-specific cleanup (value subjects, label, validity).
	 */
	override resetForm(): void {
		// Restore value (multi-aware) before super so the mixin's defaultValue
		// reset doesn't clobber the array-shape we need.
		this.value = this.selectDefaultValue
		this.isValid = true
		this.userInteracted = false
		super.resetForm()
		if (this.inputRef) {
			this.inputRef.error = false
			this.inputRef.hint = this.hint
		}
	}

	/** Back-compat alias for callers that used the previous public API. */
	public reset(): void {
		this.resetForm()
	}

	render(): TemplateResult {
		// Never during initial render or while the dropdown is open.
		const showErrors = !this.isValid && this.shouldShowError() && !this.isOpen
		const selectedOptions = this.options?.filter(option =>
			this.multi ? this.selectedValues$.value.includes(option.value) : option.value === this.selectedValue$.value,
		) ?? []

		const caretIcon = this.isOpen
			? html`<span class="absolute right-3 top-1/2 transform -translate-y-1/2">▲</span>`
			: html`<span class="absolute right-3 top-1/2 transform -translate-y-1/2">▼</span>`

		return html`
			<div class="relative ${this.disabled ? 'opacity-60 cursor-not-allowed' : ''}">
				<sch-input
					.name=${this.name ?? ''}
					tabIndex=${this.disabled ? '-1' : '0'}
					class="trigger"
					role="combobox"
					aria-haspopup="listbox"
					aria-expanded=${this.isOpen}
					aria-controls="options"
					aria-autocomplete="none"
					aria-required=${this.required}
					aria-activedescendant=${ifDefined(this.focusedOptionId || undefined)}
					aria-disabled=${this.disabled}
					aria-label=${ifDefined(this.ariaLabel || this.label || this.placeholder || undefined)}
					.label=${this.label}
					.placeholder=${this.placeholder}
					.value=${selectedOptions.map(option => option.label || option.textContent || '').join(', ') || this.placeholder}
					.required=${this.required}
					.disabled=${this.disabled}
					.hint=${showErrors ? this.validationMessage : this.hint}
					.error=${showErrors}
					.validateOn=${this.validateOn}
					.size=${this.size}
					.readonly=${true}
					clickable
					@click=${(e: MouseEvent) => {
						if (this.disabled) {
							e.preventDefault()
							e.stopPropagation()
							return
						}

						// Opening doesn't count as user interaction (and skips validation) — closing does.
						if (!this.isOpen) {
							this.openDropdown(false)
						} else {
							this.userInteracted = true
							this.closeDropdown()
						}
					}}
				>
					${caretIcon}
				</sch-input>

				${
					this.isOpen
						? html`
								<div class="fixed inset-0 z-10" @click=${this.closeDropdown} tabindex="-1" aria-hidden="true"></div>
							`
						: ''
				}

				<ul
					id="options"
					role="listbox"
					aria-multiselectable=${this.multi}
					class=${classMap({
						'absolute min-w-full w-full z-20 mt-1 rounded-lg shadow-lg': true,
						hidden: !this.isOpen,
					})}
					${color({
						bgColor: SchmancyTheme.sys.color.surface.low,
						color: SchmancyTheme.sys.color.surface.on,
					})}
				>
					<slot
						@slotchange=${() => {
							this.options$.next(this.options)
							// The rendered caption reads the assigned options after the slot settles.
							this.updateComplete.then(() => this.requestUpdate())
						}}
					></slot>
				</ul>
			</div>
		`
	}
}

// 'select' isn't exported here — it conflicts with the store's select decorator.

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-select': SchmancySelect
	}
}
